import {Button} from '@/components/ui/button';
import {Loader2} from 'lucide-react';
import {useState} from 'react';

interface BehavioralPopupFormProps {
  onSubmit: (email: string) => void;
  onDismiss: () => void;
  isSubmitting: boolean;
  error: string | null;
  isSuccess: boolean;
  /** Called when the email input receives focus. */
  onFormFocus?: () => void;
}

/**
 * Single-field email capture form for the behavioral trigger popup.
 * Location is resolved server-side via IP — no region picker needed.
 */
function BehavioralPopupForm({
  onSubmit,
  onDismiss,
  isSubmitting,
  error,
  isSuccess,
  onFormFocus,
}: BehavioralPopupFormProps) {
  const [email, setEmail] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    onSubmit(email);
  };

  if (isSuccess) {
    return (
      <div className="text-center py-2">
        <p className="text-lg font-semibold text-slate-900">You're in!</p>
        <p className="text-sm text-slate-600 mt-1">
          We'll email you when new races show up near you.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
          <svg
            className="w-5 h-5 text-slate-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
            />
          </svg>
        </div>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          onFocus={onFormFocus}
          placeholder="your.email@example.com"
          required
          disabled={isSubmitting}
          className="w-full pl-12 pr-4 py-3 rounded-xl border border-slate-200 bg-slate-50 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 focus:bg-white outline-none transition-all disabled:opacity-50 disabled:cursor-not-allowed"
        />
      </div>

      {error && (
        <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">
          {error}
        </div>
      )}

      <Button
        type="submit"
        size="lg"
        disabled={isSubmitting}
        className="w-full bg-primary hover:bg-primary/90 text-white rounded-xl py-4 text-base font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
        data-mp-element-name="behavioral-popup-submit"
        data-mp-cta-location="behavioral-popup"
      >
        {isSubmitting ? (
          <>
            <Loader2 className="w-5 h-5 mr-2 animate-spin" />
            Joining...
          </>
        ) : (
          'Alert Me'
        )}
      </Button>

      <button
        type="button"
        onClick={onDismiss}
        className="w-full text-sm text-slate-500 hover:text-slate-700 transition-colors cursor-pointer"
        data-mp-element-name="behavioral-popup-dismiss"
        data-mp-cta-location="behavioral-popup"
      >
        I'm good
      </button>
    </form>
  );
}

export {BehavioralPopupForm};
